import { useMemo, useState } from 'react';
import {
  IRRIGATION_TYPE_CLASSES,
  IRRIGATION_TYPE_LABEL,
} from '../constants/irrigationTypeLayers';
import { useTheme } from '../theme/ThemeContext';

function formatHectares(hectares: number): string {
  return `${hectares.toLocaleString('en-US', { maximumFractionDigits: 0 })} ha`;
}

function donutSlice(
  cx: number,
  cy: number,
  inner: number,
  outer: number,
  start: number,
  end: number,
): string {
  const large = end - start > Math.PI ? 1 : 0;
  const xo0 = cx + outer * Math.cos(start);
  const yo0 = cy + outer * Math.sin(start);
  const xo1 = cx + outer * Math.cos(end);
  const yo1 = cy + outer * Math.sin(end);
  const xi1 = cx + inner * Math.cos(end);
  const yi1 = cy + inner * Math.sin(end);
  const xi0 = cx + inner * Math.cos(start);
  const yi0 = cy + inner * Math.sin(start);
  return `M ${xo0} ${yo0} A ${outer} ${outer} 0 ${large} 1 ${xo1} ${yo1} L ${xi1} ${yi1} A ${inner} ${inner} 0 ${large} 0 ${xi0} ${yi0} Z`;
}

/** Nationwide irrigation-type area for Sep 2026 H2. Hover a class to read hectares. */
export default function IrrigationAreaChart() {
  const { isDark } = useTheme();
  const [activeValue, setActiveValue] = useState<number | null>(null);

  const slices = useMemo(() => {
    const drawn = IRRIGATION_TYPE_CLASSES.filter((row) => row.hectares > 0);
    const total = drawn.reduce((sum, row) => sum + row.hectares, 0);
    let cursor = -Math.PI / 2;
    return drawn.map((row) => {
      const sweep = (row.hectares / total) * Math.PI * 2;
      const gap = sweep > 0.08 ? 0.012 : 0;
      const start = cursor + gap / 2;
      const end = cursor + sweep - gap / 2;
      cursor += sweep;
      return { ...row, start, end };
    });
  }, []);

  const active = IRRIGATION_TYPE_CLASSES.find((row) => row.value === activeValue) ?? null;
  const muted = isDark ? 'text-[#8b949e]' : 'text-[#6b7280]';
  const strong = isDark ? 'text-[#e6edf3]' : 'text-[#111827]';

  return (
    <div
      className={`rounded-lg border px-2.5 py-2 ${
        isDark ? 'border-[#2d3748] bg-[#1c2432]' : 'border-[#e5e7eb] bg-white'
      }`}
      onPointerLeave={() => setActiveValue(null)}
    >
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <p className={`text-sm font-semibold ${strong}`}>Area by irrigation type</p>
        <p className={`text-xs ${muted}`}>{IRRIGATION_TYPE_LABEL}</p>
      </div>

      <div className="flex items-center gap-3">
        <svg
          viewBox="0 0 120 120"
          className="h-[120px] w-[120px] shrink-0"
          role="img"
          aria-label="Irrigation type area"
        >
          {slices.map((slice) => {
            const selected = activeValue === slice.value;
            const dimmed = activeValue != null && !selected;
            return (
              <path
                key={slice.value}
                d={donutSlice(60, 60, 34, 54, slice.start, slice.end)}
                fill={slice.color}
                opacity={dimmed ? 0.35 : 1}
                className="cursor-pointer outline-none"
                onPointerEnter={() => setActiveValue(slice.value)}
                onClick={() => setActiveValue(slice.value)}
              >
                <title>{`${slice.name}: ${formatHectares(slice.hectares)}`}</title>
              </path>
            );
          })}
        </svg>

        <div className="min-w-0 flex-1">
          {active ? (
            <>
              <p className={`text-sm font-semibold leading-snug ${strong}`}>{active.name}</p>
              <p className={`mt-1 text-lg font-semibold tabular-nums ${isDark ? 'text-[#34d399]' : 'text-[#008540]'}`}>
                {formatHectares(active.hectares)}
              </p>
              {!active.drawn && (
                <p className={`mt-1 text-xs ${muted}`}>Not drawn on the map</p>
              )}
            </>
          ) : (
            <p className={`text-xs leading-snug ${muted}`}>
              Hover a class to see its area. No active crop is not drawn on the map.
            </p>
          )}
        </div>
      </div>

      <ul className="mt-3 space-y-1">
        {IRRIGATION_TYPE_CLASSES.map((row) => {
          const selected = activeValue === row.value;
          return (
            <li key={row.value}>
              <button
                type="button"
                onPointerEnter={() => setActiveValue(row.value)}
                onClick={() => setActiveValue(row.value)}
                className={`flex w-full items-center gap-2 rounded px-1 py-1 text-left ${
                  selected ? (isDark ? 'bg-white/[0.06]' : 'bg-[#f3f4f6]') : ''
                }`}
              >
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-sm border border-black/10"
                  style={{ backgroundColor: row.color }}
                />
                <span className={`min-w-0 flex-1 truncate text-xs ${selected ? strong : muted}`}>
                  {row.name}{row.drawn ? '' : ' (not drawn)'}
                </span>
                <span className={`shrink-0 text-sm font-semibold tabular-nums ${selected ? (isDark ? 'text-[#34d399]' : 'text-[#008540]') : strong}`}>
                  {selected ? formatHectares(row.hectares) : `${row.share}%`}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
